/**
 * Renders the administration side navigation used to access configuration
 * and management modules throughout the application. Supports dynamic menu
 * generation, active state management, collapsible navigation behavior,
 * and icon-based navigation for configuration-driven administration pages.
 */
import React from "react";
import {
  BarChart03,
  BookClosed,
  CheckVerified01,
  Grid01,
  Pencil01,
  Settings03,
  Sliders01,
  Star06,
  Users01,
} from "@untitledui/icons";
import FeatureNameConfig from "./featureNameConfiguration/FeatureNameConfig";
import QuickAppsConfig from "./quickAppsConfiguration/QuickAppsConfig";

type Configuration = {
  configurationCode: string;
  configurationName: string;
};

type Props = {
  drawerOpen: boolean;
  active: string;
  setActive: (val: string) => void;
  configurations: Configuration[];
};

const getIcon = (configurationCode: string) => {
  switch (configurationCode) {
    case "onboarding.config":
      return <Users01 className="h-4 w-4" />;

    case "access.config":
      return <Grid01 className="h-4 w-4" />;

    case "widget.config":
      return <Sliders01 className="h-4 w-4" />;

    case "common.config":
      return <Settings03 className="h-4 w-4" />;

    case "user.config":
      return <CheckVerified01 className="h-4 w-4" />;
    
    case "quickapps.config":
      return <Star06 className="h-4 w-4" />
      
   case "featurename.config":
      return <Pencil01 className="h-4 w-4" />
    
      case "accessreport.config":
      return <BookClosed className="h-4 w-4" />;

    default:
      return <BarChart03 className="h-4 w-4" />;
  }
};

const SideNav: React.FC<Props> = ({
  drawerOpen,
  active,
  setActive,
  configurations,
}) => {
  return (
    <aside
      className={`fixed left-0 top-14 h-[calc(100vh-56px)] border-r z-[900] transition-all duration-500 bg-white border-gray-200 ${
        drawerOpen ? "w-[260px]" : "w-[64px]"
      }`}
    >
      <nav className="mt-2 px-2 space-y-1">
        {configurations.map((config, index) => (
          <div
            key={`${config.configurationCode}-${index}`}
            onClick={() => setActive(config.configurationCode)}
            className={`flex items-center cursor-pointer rounded-md text-sm font-medium transition-all ${
              drawerOpen ? "gap-3 px-3 py-2" : "justify-center h-10"
            } ${
              active === config.configurationCode
                ? "bg-[#fff4ec] text-orange-600"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            {getIcon(config.configurationCode)}

            {drawerOpen && (
              <span>{config.configurationName}</span>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default SideNav;